import { Component, type ErrorInfo, type ReactNode } from "react";
import { WifiOff } from "lucide-react";
import { Button } from "@/components/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

function isDynamicImportError(error: Error): boolean {
  const message = error.message ?? "";
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("error loading dynamically imported module")
  );
}

/**
 * Captura fallos de lazy import (típico sin red en dev) y muestra un mensaje útil.
 */
export class LazyRouteBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      isChunkError: isDynamicImportError(error),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[LazyRouteBoundary]", error, info.componentStack);
  }

  private handleRetry = () => {
    if (!navigator.onLine) return;
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.state.isChunkError) {
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center">
          <WifiOff className="h-12 w-12 text-muted-foreground" />
          <div className="space-y-2 max-w-md">
            <h2 className="text-lg font-semibold">Sin conexión</h2>
            <p className="text-sm text-muted-foreground">
              No se pudo cargar esta sección sin internet. Vuelve a conectarte y
              recarga la página, o regresa al punto de venta si ya estaba abierto.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="default"
              onClick={this.handleRetry}
              disabled={!navigator.onLine}
            >
              Recargar
            </Button>
            <Button variant="outline" onClick={() => window.history.back()}>
              Volver
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="text-sm text-destructive">Ocurrió un error al cargar la vista.</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Reintentar
        </Button>
      </div>
    );
  }
}
